/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Data extraction", description = "Write ad-platform audience data back to your warehouse.", lastUpdatedDate = new Date(1793232000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Data extraction",
        description: "Write ad-platform audience data back to your warehouse.",
        lastUpdatedDate: "Thu Oct 29 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "What data extraction does",
          "depth": 0,
          "id": "what-data-extraction-does"
        }, {
          "value": "Enable data extraction",
          "depth": 0,
          "id": "enable-data-extraction"
        }, {
          "value": "What lands in your warehouse",
          "depth": 0,
          "id": "what-lands-in-your-warehouse"
        }, {
          "value": "Per-destination tables",
          "depth": 1,
          "id": "per-destination-tables"
        }, {
          "value": "Combined view",
          "depth": 1,
          "id": "combined-view"
        }, {
          "value": "How the daily job behaves",
          "depth": 0,
          "id": "how-the-daily-job-behaves"
        }, {
          "value": "Verify it worked",
          "depth": 0,
          "id": "verify-it-worked"
        }, {
          "value": "Supported destinations",
          "depth": 0,
          "id": "supported-destinations"
        }, {
          "value": "Limitations",
          "depth": 0,
          "id": "limitations"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      em: "em",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      h2: "h2",
      img: "img",
      ol: "ol",
      li: "li",
      code: "code",
      h3: "h3",
      ul: "ul",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {FeatureFlag} = _components;
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    return _jsxs(_Fragment, {
      children: [_jsx(FeatureFlag, {
        featureName: "Data extraction"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Data extraction writes audience data from your ad platforms back to your warehouse, so you can query and report on it alongside the rest of your data."
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["A ", _jsx(_components.a, {
                href: "/syncs/lightning-sync-engine",
                children: "Lightning sync engine"
              }), " source on Snowflake, BigQuery, Postgres, or Microsoft Fabric, plus a connected ", _jsx(_components.a, {
                href: "#supported-destinations",
                children: "supported ad destination"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "what-data-extraction-does",
        children: "What data extraction does"
      }), "\n", _jsx(_components.p, {
        children: "Ad platforms hold audience metadata — such as the estimated size of each audience — that otherwise lives only in the platform's own UI. Data extraction pulls that metadata on a daily schedule and writes it to your warehouse. From there, you can join it to your other data, track it over time, and report on it."
      }), "\n", _jsxs(_components.p, {
        children: ["Today it produces one report: the ", _jsx(_components.strong, {
          children: "Audience size report"
        }), ", a daily snapshot of each audience's estimated size in the ad platform."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/data-extraction-outline.png",
          alt: "Data extraction outline"
        })
      }), "\n", _jsx(_components.h2, {
        id: "enable-data-extraction",
        children: "Enable data extraction"
      }), "\n", _jsx(_components.p, {
        children: "Data extraction is configured per destination."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the destination's page and open the ", _jsx(_components.strong, {
            children: "Data extraction"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Turn on ", _jsx(_components.strong, {
            children: "Enable data extraction"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Which data system should we send this data to?"
          }), ", select the source Hightouch writes the report to. Hightouch writes to the ", _jsx(_components.code, {
            children: "hightouch_audit"
          }), " schema in that source."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Audience size report"
        }), " is selected automatically, since it's the only report available. After you enable extraction, the status shows ", _jsx(_components.strong, {
          children: "Pending"
        }), " until the first run, and the report runs once a day."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/data-extraction-configured.png",
          alt: "The Data extraction tab configured for a Google Ads destination"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The source you choose must be on the ", _jsx(_components.a, {
          href: "/syncs/lightning-sync-engine",
          children: "Lightning sync engine"
        }), ", which gives Hightouch write-back access to your warehouse, and must be Snowflake, BigQuery, Postgres, or Microsoft Fabric. Sources that don't meet both conditions don't appear in the list."]
      }), "\n", _jsx(_components.h2, {
        id: "what-lands-in-your-warehouse",
        children: "What lands in your warehouse"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch writes to the ", _jsx(_components.code, {
          children: "hightouch_audit"
        }), " schema in the source you selected."]
      }), "\n", _jsx(_components.h3, {
        id: "per-destination-tables",
        children: "Per-destination tables"
      }), "\n", _jsxs(_components.p, {
        children: ["Each enabled destination gets its own table, named ", _jsx(_components.code, {
          children: "{destination_type}_{destination_id}_audiences"
        }), ". Dashes in the destination type become underscores, so a Google Ads destination writes to ", _jsx(_components.code, {
          children: "google_ads_{id}_audiences"
        }), ". Each table's columns mirror the ad platform's API response as closely as possible, so you can transform the data to fit your needs."]
      }), "\n", _jsx(_components.h3, {
        id: "combined-view",
        children: "Combined view"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch also builds a view, ", _jsx(_components.code, {
          children: "external_audiences_metadata_v1"
        }), ", that unions every destination table into one standardized schema:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ad_account_id"
              })
            }), _jsx(_components.td, {
              children: "The ad account the audience belongs to."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "audience_id"
              })
            }), _jsx(_components.td, {
              children: "The audience's ID in the ad platform."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "audience_name"
              })
            }), _jsx(_components.td, {
              children: "The audience's name in the ad platform."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "audience_size"
              })
            }), _jsx(_components.td, {
              children: "The estimated audience size at the time of the snapshot."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ht_fetched_at"
              })
            }), _jsx(_components.td, {
              children: "When Hightouch fetched the snapshot."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ht_destination"
              })
            }), _jsx(_components.td, {
              children: "The Hightouch destination type."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ht_destination_id"
              })
            }), _jsx(_components.td, {
              children: "The Hightouch destination ID."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ht_sync_id"
              })
            }), _jsxs(_components.td, {
              children: ["The Hightouch sync feeding the audience, or ", _jsx(_components.code, {
                children: "null"
              }), " when no sync feeds it."]
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["The view is versioned. Schema changes ship as a new version, such as ", _jsx(_components.code, {
          children: "external_audiences_metadata_v2"
        }), ", and earlier versions keep working."]
      }), "\n", _jsx(_components.h2, {
        id: "how-the-daily-job-behaves",
        children: "How the daily job behaves"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Hightouch runs one job per enabled destination, once a day."
        }), "\n", _jsx(_components.li, {
          children: "Each job pulls every audience from the ad accounts the connected user authorized — not only the audiences Hightouch syncs to."
        }), "\n", _jsx(_components.li, {
          children: "Each run appends a new snapshot. Hightouch doesn't update or delete earlier snapshots, so the tables build a historical record over time."
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "verify-it-worked",
        children: "Verify it worked"
      }), "\n", _jsx(_components.p, {
        children: "After the first daily run, query the combined view in the source you selected:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "select"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " hightouch_audit.external_audiences_metadata_v1\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "order"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "by"
          }), " ht_fetched_at ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "desc"
          }), ";\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You should see one row per audience per snapshot, with a recent ", _jsx(_components.code, {
          children: "ht_fetched_at"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "supported-destinations",
        children: "Supported destinations"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/amazon-ads",
            children: "Amazon Ads DSP"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/facebook",
            children: "Meta Custom Audiences"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/google",
            children: "Google Ads"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/bingads",
            children: "Microsoft Ads Audiences"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/snapchat",
            children: "Snapchat"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/tiktok",
            children: "TikTok"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "limitations",
        children: "Limitations"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "The only available report is the Audience size report."
        }), "\n", _jsx(_components.li, {
          children: "Audience sizes are the ad platform's estimates, not exact counts."
        }), "\n", _jsx(_components.li, {
          children: "The report runs once a day."
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
